import { EventHandle, Quat, Vec3 } from 'playcanvas';

import { CubicSpline } from './anim/spline';
import { AnimTrack } from './anim-track';
import { Element } from './element';
import { Events } from './events';

// duck-typed subset of Splat/Model/SceneObject/Water shared by everything
// selectable and transformable in the scene
type AnimatableElement = Element & {
    entity: { getLocalPosition: () => Vec3, getLocalRotation: () => Quat, getLocalScale: () => Vec3 };
    visible: boolean;
    move: (position?: Vec3, rotation?: Quat, scale?: Vec3) => void;
};

type ObjectKey = {
    frame: number;
    position: Vec3;
    rotation: Quat;
    scale: Vec3;
    visible: boolean;
};

/**
 * Per-object animation track: keyframes the target element's local transform
 * and visibility. Mirrors CameraAnimTrack's shape/lifecycle so it plugs into
 * the same timeline/track-manager machinery, but is created/destroyed
 * dynamically per animated element rather than existing as a singleton.
 */
class ObjectAnimTrack implements AnimTrack {
    private keysList: ObjectKey[] = [];
    private events: Events;
    private target: AnimatableElement;
    private onTimelineChange: ((frame: number) => void) | null = null;
    private handles: EventHandle[];

    constructor(events: Events, target: AnimatableElement) {
        this.events = events;
        this.target = target;

        this.handles = [
            events.on('timeline.time', (time: number) => this.evaluate(time)),
            events.on('timeline.frame', (frame: number) => this.evaluate(frame)),
            events.on('timeline.frames', () => this.rebuildSpline()),
            events.on('timeline.smoothness', () => this.rebuildSpline())
        ];
    }

    get keys(): readonly number[] {
        return this.keysList.map(k => k.frame);
    }

    addKey(frame: number, visibleOverride?: boolean): boolean {
        const { entity, visible } = this.target;

        const newKey: ObjectKey = {
            frame,
            position: entity.getLocalPosition().clone(),
            rotation: entity.getLocalRotation().clone(),
            scale: entity.getLocalScale().clone(),
            visible: visibleOverride ?? visible
        };

        const existingIndex = this.keysList.findIndex(k => k.frame === frame);
        if (existingIndex === -1) {
            this.keysList.push(newKey);
            this.rebuildSpline();
            this.events.fire('track.keyAdded', frame);
        } else {
            this.keysList[existingIndex] = newKey;
            this.rebuildSpline();
            this.events.fire('track.keyUpdated', frame);
        }
        return true;
    }

    removeKey(frame: number): boolean {
        const index = this.keysList.findIndex(k => k.frame === frame);
        if (index === -1) return false;
        this.keysList.splice(index, 1);
        this.rebuildSpline();
        this.events.fire('track.keyRemoved', frame);
        return true;
    }

    moveKey(fromFrame: number, toFrame: number): boolean {
        if (fromFrame === toFrame) return false;

        const index = this.keysList.findIndex(k => k.frame === fromFrame);
        if (index === -1) return false;

        const toIndex = this.keysList.findIndex(k => k.frame === toFrame);
        if (toIndex !== -1) {
            this.keysList.splice(toIndex, 1);
        }

        const movedIndex = this.keysList.findIndex(k => k.frame === fromFrame);
        this.keysList[movedIndex].frame = toFrame;
        this.rebuildSpline();
        this.events.fire('track.keyMoved', fromFrame, toFrame);
        return true;
    }

    copyKey(fromFrame: number, toFrame: number): boolean {
        if (fromFrame === toFrame) return false;

        const source = this.keysList.find(k => k.frame === fromFrame);
        if (!source) return false;

        const toIndex = this.keysList.findIndex(k => k.frame === toFrame);
        if (toIndex !== -1) {
            this.keysList.splice(toIndex, 1);
        }

        this.keysList.push({
            frame: toFrame,
            position: source.position.clone(),
            rotation: source.rotation.clone(),
            scale: source.scale.clone(),
            visible: source.visible
        });

        this.rebuildSpline();
        this.events.fire('track.keyAdded', toFrame);
        return true;
    }

    evaluate(frame: number): void {
        this.onTimelineChange?.(frame);
    }

    // apply the target's animated visibility without letting selection.ts
    // treat it as a user-initiated hide (which would deselect the target -
    // see selection.ts's onVisibility and the matching suppression there)
    private setTargetVisible(visible: boolean): void {
        this.events.fire('track.applyingAnimatedVisibility', true);
        this.target.visible = visible;
        this.events.fire('track.applyingAnimatedVisibility', false);
    }

    clear(): void {
        this.keysList.length = 0;
        this.onTimelineChange = null;
        this.events.fire('track.keysCleared');
    }

    snapshot(): ObjectKey[] {
        return this.keysList.map(k => ({
            frame: k.frame,
            position: k.position.clone(),
            rotation: k.rotation.clone(),
            scale: k.scale.clone(),
            visible: k.visible
        }));
    }

    restore(snapshot: unknown): void {
        this.keysList = (snapshot as ObjectKey[]).map(k => ({
            frame: k.frame,
            position: k.position.clone(),
            rotation: k.rotation.clone(),
            scale: k.scale.clone(),
            visible: k.visible
        }));
        this.rebuildSpline();
        this.events.fire('track.keysLoaded');
    }

    /** Used for serialization. */
    getKeys(): readonly ObjectKey[] {
        return this.keysList;
    }

    /** Used for deserialization. */
    loadKeys(keysData: ObjectKey[]): void {
        this.keysList.length = 0;
        keysData.forEach(k => this.keysList.push(k));
        this.rebuildSpline();
        this.events.fire('track.keysLoaded');
    }

    /** Unsubscribe from timeline events; called when the target element is removed. */
    destroy(): void {
        this.handles.forEach(h => h.off());
        this.handles.length = 0;
        this.onTimelineChange = null;
    }

    private rebuildSpline(): void {
        const duration = this.events.invoke('timeline.frames');
        const smoothness = this.events.invoke('timeline.smoothness');

        const ordered = this.keysList.slice()
        .filter(k => k.frame < duration)
        .sort((a, b) => a.frame - b.frame);

        // visibility is a hard cut at the nearest key at or before the current
        // frame (falls back to the earliest key if scrubbed before it)
        const visibleAt = (frame: number): boolean => {
            let v = ordered[0].visible;
            for (const k of ordered) {
                if (k.frame <= frame) v = k.visible; else break;
            }
            return v;
        };

        if (ordered.length > 1) {
            const times = ordered.map(k => k.frame);
            const points: number[] = [];
            for (const k of ordered) {
                points.push(k.position.x, k.position.y, k.position.z);
                points.push(k.rotation.x, k.rotation.y, k.rotation.z, k.rotation.w);
                points.push(k.scale.x, k.scale.y, k.scale.z);
            }

            const spline = CubicSpline.fromPointsLooping(duration, times, points, smoothness);
            const result: number[] = [];
            const pos = new Vec3();
            const rot = new Quat();
            const scl = new Vec3();

            this.onTimelineChange = (frame: number) => {
                spline.evaluate(frame, result);
                pos.set(result[0], result[1], result[2]);
                rot.set(result[3], result[4], result[5], result[6]).normalize();
                scl.set(result[7], result[8], result[9]);
                this.target.move(pos, rot, scl);
                this.setTargetVisible(visibleAt(frame));
            };
        } else if (ordered.length === 1) {
            // single key: hold that pose constantly, no interpolation possible
            const k = ordered[0];
            this.onTimelineChange = () => {
                this.target.move(k.position, k.rotation, k.scale);
                this.setTargetVisible(k.visible);
            };
        } else {
            this.onTimelineChange = null;
        }

        this.evaluate(this.events.invoke('timeline.frame'));
    }
}

export { ObjectAnimTrack, ObjectKey, AnimatableElement };
